import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import axiosApiInstance from '@/api/shared/axios-instance';
import URL_FACTORY from '@/helpers/url-helper';
import { getMasterPhotoById } from '@/helpers/images-helper';
import useTeam from '@/hooks/use-team';
import FantasyLaLigaIcon from '@/components/shared/icons/fantasy-la-liga-icon';
import MisterFantasyIcon from '@/components/shared/icons/MisterFantasyIcon';
import FantasyMarcaIcon from '@/components/shared/icons/FantasyMarcaIcon';
import BiwengerIcon from '@/components/shared/icons/biwenger-icon';
import { toPriceInEuros } from '@/helpers/prices-helper';

const SummaryBox = ({ title, total, average }) => (
    <Box
        sx={{
            flex: 1,
            p: 1.5,
            backgroundColor: 'rgba(161, 38, 218, 0.1)',
            borderRadius: 1,
            textAlign: 'center',
        }}
    >
        <Typography variant="body2" sx={{ color: '#a126da', fontWeight: 600, fontSize: '15px' }}>
            {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px', mt: 0.5 }}>
            Total
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 500 }}>
            {total}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px', mt: 0.5 }}>
            Promedio
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 500 }}>
            {average}
        </Typography>
    </Box>
);

const EstadoPlantillaModal = ({ open, onClose, teamId }) => {
    const [selectedFantasy, setSelectedFantasy] = useState(0);
    const [loading, setLoading] = useState(false);
    const [jugadoresData, setJugadoresData] = useState([]);
    const [orderBy, setOrderBy] = useState('marketValue');
    const [order, setOrder] = useState('desc');
    const [anchorEl, setAnchorEl] = useState(null);

    const { teamName } = useTeam(teamId);

    const size = 20;

    const fantasyOptions = [
        {
            id: 'fantasy-oficial',
            index: 0,
            label: 'La Liga Fantasy',
            icon: <FantasyLaLigaIcon iconWidth={size} iconHeight={size} />,
            pointsKey: null,
        },
        {
            id: 'mister-fantasy',
            index: 1,
            label: 'Mister Fantasy',
            icon: <MisterFantasyIcon width={size} height={size} />,
            pointsKey: null,
        },
        {
            id: 'fantasy-marca',
            index: 2,
            label: 'Fantasy Marca',
            icon: <FantasyMarcaIcon width={size} height={size} />,
            pointsKey: null,
        },
        {
            id: 'biwenger-as',
            index: 3,
            label: 'Biwenger - AS',
            icon: <BiwengerIcon width={size} height={size} />,
            pointsKey: 'pointsAs',
        },
        {
            id: 'biwenger-sofascore',
            index: 4,
            label: 'Biwenger - SOFASCORE',
            icon: <BiwengerIcon width={size} height={size} />,
            pointsKey: 'pointsSofascore',
        },
        {
            id: 'biwenger-stats',
            index: 5,
            label: 'Biwenger - STATS',
            icon: <BiwengerIcon width={size} height={size} />,
            pointsKey: 'pointsStats',
        },
        {
            id: 'biwenger-average',
            index: 6,
            label: 'Biwenger - AS + SOFA',
            icon: <BiwengerIcon width={size} height={size} />,
            pointsKey: 'pointsAverage',
        },
        {
            id: 'biwenger-social',
            index: 7,
            label: 'Biwenger - SOCIAL',
            icon: <BiwengerIcon width={size} height={size} />,
            pointsKey: 'pointsSocial',
        },
    ];

    const handleSelectorClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSelectorClose = () => {
        setAnchorEl(null);
    };

    const handleFantasySelect = (option) => {
        setSelectedFantasy(option.index);
        setOrderBy('marketValue');
        setOrder('desc');
        handleSelectorClose();
    };

    const getCurrentOption = () => {
        return fantasyOptions.find((option) => option.index === selectedFantasy) || fantasyOptions[0];
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    useEffect(() => {
        if (open && teamId) {
            fetchEstadoPlantilla();
        }
    }, [open, teamId]);

    const fetchEstadoPlantilla = async () => {
        try {
            setLoading(true);
            const response = await axiosApiInstance.post(URL_FACTORY.ObtenerEstadoPlantilla, {
                teamId: teamId,
            });

            if (response.data && response.data.jugadores) {
                setJugadoresData(response.data.jugadores);
            }
        } catch (error) {
            console.error('Error fetching estado plantilla:', error);
        } finally {
            setLoading(false);
        }
    };

    const getSortedData = () => {
        return [...jugadoresData].sort((a, b) => {
            const aValue = getPlayerValue(a, orderBy);
            const bValue = getPlayerValue(b, orderBy);

            if (order === 'asc') {
                return aValue - bValue;
            } else {
                return bValue - aValue;
            }
        });
    };

    const getPlayerValue = (jugador, property) => {
        const fantasyData = getFantasyData(jugador);

        // Si es puntos y estamos en Biwenger, usar la key específica
        if (property === 'points' && selectedFantasy >= 3) {
            const currentOption = fantasyOptions.find((opt) => opt.index === selectedFantasy);
            if (currentOption && currentOption.pointsKey) {
                return fantasyData[currentOption.pointsKey] || 0;
            }
        }

        return fantasyData[property] || 0;
    };

    const getFantasyData = (jugador) => {
        switch (selectedFantasy) {
            case 0:
                return jugador.fantasyOficial || {};
            case 1:
                return jugador.misterFantasy || {};
            case 2:
                return jugador.fantasyMarca || {};
            case 3:
            case 4:
            case 5:
            case 6:
            case 7:
                // Todas las opciones de Biwenger usan el mismo objeto
                return jugador.biwenger || {};
            default:
                return jugador.fantasyOficial || {};
        }
    };

    const formatValue = (value, type) => {
        switch (type) {
            case 'marketValue':
                return Number.isInteger(value) ? toPriceInEuros(value) : toPriceInEuros(value.toFixed(2));
            case 'subida':
                return Number.isInteger(value) ? toPriceInEuros(value) : toPriceInEuros(value.toFixed(2));
            case 'points':
                // Si tiene decimales, redondear a 2, si no, retornar el entero
                return Number.isInteger(value) ? value : value.toFixed(2);
            default:
                return value;
        }
    };

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={`Estado de Plantilla - ${teamName}`}
            maxWidth="lg"
            width="95%"
            paperSx={{
                minHeight: '600px',
            }}
            contentSx={{ pt: 2 }}
            padding={0.5}
        >
            {loading ? (
                <Box
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
                >
                    <CircularProgress sx={{ color: '#a126da' }} />
                </Box>
            ) : (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleSelectorClick}
                            sx={{
                                width: '100%',
                                justifyContent: 'space-between',
                                textTransform: 'none',
                                height: '40px',
                                border: '1px solid',
                                borderColor: 'border.main',
                                borderRadius: '4px',
                                padding: '0 16px',
                                '&:hover': {
                                    border: '1px solid rgba(0, 0, 0, 0.87)',
                                },
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                {getCurrentOption().icon}
                                <Typography variant="body2" fontSize={12}>
                                    {getCurrentOption().label}
                                </Typography>
                            </Box>
                            <KeyboardArrowDown />
                        </Button>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleSelectorClose}
                            PaperProps={{
                                sx: {
                                    minWidth: 250,
                                    maxHeight: 400,
                                },
                            }}
                        >
                            {fantasyOptions.map((option) => (
                                <MenuItem
                                    key={option.id}
                                    onClick={() => handleFantasySelect(option)}
                                    selected={option.index === selectedFantasy}
                                >
                                    <ListItemIcon>{option.icon}</ListItemIcon>
                                    <ListItemText primary={option.label} />
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    {jugadoresData.length > 0 ? (
                        <>
                            <TableContainer
                                component={Paper}
                                sx={{ maxHeight: 350, mb: 2, overflowX: 'hidden' }}
                            >
                                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: 'background.paper',
                                                    fontSize: '12px',
                                                    padding: '8px 4px',
                                                    width: '40px',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                #
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: 'background.paper',
                                                    fontSize: '12px',
                                                    padding: '8px 4px',
                                                    width: 'auto',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                Jugador
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: 'background.paper',
                                                    fontSize: '12px',
                                                    padding: '8px 4px',
                                                    width: '90px',
                                                    textAlign: 'center',
                                                }}
                                                sortDirection={orderBy === 'marketValue' ? order : false}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === 'marketValue'}
                                                    direction={orderBy === 'marketValue' ? order : 'asc'}
                                                    onClick={() => handleSort('marketValue')}
                                                    sx={{
                                                        fontSize: '12px',
                                                        width: '100%',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    Valor Mercado
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: 'background.paper',
                                                    fontSize: '12px',
                                                    padding: '8px 4px',
                                                    width: '70px',
                                                    textAlign: 'center',
                                                }}
                                                sortDirection={orderBy === 'points' ? order : false}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === 'points'}
                                                    direction={orderBy === 'points' ? order : 'asc'}
                                                    onClick={() => handleSort('points')}
                                                    sx={{
                                                        fontSize: '12px',
                                                        width: '100%',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    Ptos
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: 'background.paper',
                                                    fontSize: '12px',
                                                    padding: '8px 4px',
                                                    width: '80px',
                                                    textAlign: 'center',
                                                }}
                                                sortDirection={orderBy === 'subida' ? order : false}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === 'subida'}
                                                    direction={orderBy === 'subida' ? order : 'asc'}
                                                    onClick={() => handleSort('subida')}
                                                    sx={{
                                                        fontSize: '12px',
                                                        width: '100%',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    Subida
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getSortedData().map((jugador, index) => (
                                            <TableRow key={jugador.id} hover>
                                                <TableCell
                                                    sx={{
                                                        fontSize: '12px',
                                                        padding: '6px 4px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontSize: '12px',
                                                        padding: '6px 4px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <CustomImageComponent
                                                            src={getMasterPhotoById(jugador.id)}
                                                            alt={jugador.nombre}
                                                            width={24}
                                                            height={24}
                                                            sx={{ borderRadius: '50%', flexShrink: 0 }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: '12px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {jugador.nombre}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontSize: '12px',
                                                        padding: '6px 4px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 500, fontSize: '12px' }}
                                                    >
                                                        {formatValue(
                                                            getPlayerValue(jugador, 'marketValue'),
                                                            'marketValue'
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontSize: '14px',
                                                        padding: '6px 4px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 500, fontSize: '12px' }}
                                                    >
                                                        {formatValue(
                                                            getPlayerValue(jugador, 'points'),
                                                            'points'
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontSize: '14px',
                                                        padding: '6px 4px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 500, fontSize: '12px' }}
                                                    >
                                                        {formatValue(
                                                            getPlayerValue(jugador, 'subida'),
                                                            'subida'
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <SummaryBox
                                    title="Valor Mercado"
                                    total={formatValue(
                                        getSortedData().reduce(
                                            (sum, player) => sum + getPlayerValue(player, 'marketValue'),
                                            0
                                        ),
                                        'marketValue'
                                    )}
                                    average={formatValue(
                                        getSortedData().length > 0
                                            ? getSortedData().reduce(
                                                  (sum, player) =>
                                                      sum + getPlayerValue(player, 'marketValue'),
                                                  0
                                              ) / getSortedData().length
                                            : 0,
                                        'marketValue'
                                    )}
                                />

                                <SummaryBox
                                    title="Puntos"
                                    total={formatValue(
                                        getSortedData().reduce(
                                            (sum, player) => sum + getPlayerValue(player, 'points'),
                                            0
                                        ),
                                        'points'
                                    )}
                                    average={formatValue(
                                        getSortedData().length > 0
                                            ? getSortedData().reduce(
                                                  (sum, player) => sum + getPlayerValue(player, 'points'),
                                                  0
                                              ) / getSortedData().length
                                            : 0,
                                        'points'
                                    )}
                                />

                                <SummaryBox
                                    title="Subida"
                                    total={formatValue(
                                        getSortedData().reduce(
                                            (sum, player) => sum + getPlayerValue(player, 'subida'),
                                            0
                                        ),
                                        'subida'
                                    )}
                                    average={formatValue(
                                        getSortedData().length > 0
                                            ? getSortedData().reduce(
                                                  (sum, player) => sum + getPlayerValue(player, 'subida'),
                                                  0
                                              ) / getSortedData().length
                                            : 0,
                                        'subida'
                                    )}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '400px',
                            }}
                        >
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                No hay datos disponibles
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </CustomModal>
    );
};

export default EstadoPlantillaModal;
